import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createContaPagar } from '../../../services/contaPagarService';
import { getAll as getAllContas } from '../../../services/contaService';
import ConfirmModal from '../../../components/ConfirmModal';

const ContaPagarCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contas, setContas] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [formData, setFormData] = useState({
    descricao: '',
    dataVencimento: '',
    valor: '',
    parcela: 1,
    totalParcelas: 1,
    conta: '',
    cadastradoPor: 'usuario.atual', // TODO: pegar usuário logado
    pago: 'Não'
  });
  const [errors, setErrors] = useState({});

  // Carregar contas
  useEffect(() => {
    const loadContas = async () => {
      try {
        const contasData = await getAllContas();
        setContas(contasData);
      } catch (err) {
        console.error('Erro ao carregar contas:', err);
      }
    };

    loadContas();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.dataVencimento) {
      newErrors.dataVencimento = 'Data de vencimento é obrigatória';
    }

    if (!formData.valor || parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.conta) {
      newErrors.conta = 'Conta é obrigatória';
    }

    if (!formData.parcela || parseInt(formData.parcela) < 1) {
      newErrors.parcela = 'Parcela deve ser maior que zero';
    }

    if (!formData.totalParcelas || parseInt(formData.totalParcelas) < 1) {
      newErrors.totalParcelas = 'Total de parcelas deve ser maior que zero';
    }

    if (parseInt(formData.parcela) > parseInt(formData.totalParcelas)) {
      newErrors.parcela = 'Parcela não pode ser maior que o total de parcelas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const dataToSubmit = {
      ...formData,
      valor: parseFloat(formData.valor),
      parcela: parseInt(formData.parcela),
      totalParcelas: parseInt(formData.totalParcelas),
      conta: parseInt(formData.conta)
    };

    setPendingData(dataToSubmit);
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    setShowConfirmModal(false);
    
    try {
      await createContaPagar(pendingData);
      navigate('/financeiro/contas-pagar', { 
        state: { 
          message: 'Conta a pagar criada com sucesso!', 
          type: 'success' 
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.message || 'Erro ao criar conta a pagar' 
      });
    } finally {
      setLoading(false);
      setPendingData(null);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
    setPendingData(null);
  };

  const handleCancel = () => {
    navigate('/financeiro/contas-pagar');
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center mb-4">
        <button
          onClick={handleCancel}
          className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" />
        </button>
        <h2 className="text-xl font-bold">Cadastrar Nova Conta a Pagar</h2>
      </div>
      {/* `noValidate`: a validação em português deste formulário é a que o
          usuário deve ver — o balão nativo do navegador some sozinho, sai no
          idioma do navegador e mostra um campo por vez. */}


      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.submit && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Descrição */}
          <div className="md:col-span-2">
            <label htmlFor="descricao" className="block text-sm font-medium mb-1">
              Descrição *
            </label>
            <input
              type="text"
              id="descricao"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.descricao ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Fornecedor - Material de Escritório"
            />
            {errors.descricao && (
              <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
            )}
          </div>

          {/* Data de Vencimento */}
          <div>
            <label htmlFor="dataVencimento" className="block text-sm font-medium mb-1">
              Data de Vencimento *
            </label>
            <input
              type="date"
              id="dataVencimento"
              value={formData.dataVencimento}
              onChange={(e) => handleInputChange('dataVencimento', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.dataVencimento ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dataVencimento && (
              <p className="mt-1 text-sm text-red-600">{errors.dataVencimento}</p>
            )}
          </div>

          {/* Valor */}
          <div>
            <label htmlFor="valor" className="block text-sm font-medium mb-1">
              Valor *
            </label>
            <input
              type="number"
              id="valor"
              step="0.01"
              min="0"
              value={formData.valor}
              onChange={(e) => handleInputChange('valor', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.valor ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0,00"
            />
            {errors.valor && (
              <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
            )}
          </div>

          {/* Parcela */}
          <div>
            <label htmlFor="parcela" className="block text-sm font-medium mb-1">
              Parcela *
            </label>
            <input
              type="number"
              id="parcela"
              min="1"
              value={formData.parcela}
              onChange={(e) => handleInputChange('parcela', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.parcela ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.parcela && (
              <p className="mt-1 text-sm text-red-600">{errors.parcela}</p>
            )}
          </div>

          {/* Total de Parcelas */}
          <div>
            <label htmlFor="totalParcelas" className="block text-sm font-medium mb-1">
              Total de Parcelas *
            </label>
            <input
              type="number"
              id="totalParcelas"
              min="1"
              value={formData.totalParcelas}
              onChange={(e) => handleInputChange('totalParcelas', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.totalParcelas ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.totalParcelas && (
              <p className="mt-1 text-sm text-red-600">{errors.totalParcelas}</p>
            )}
          </div>

          {/* Conta */}
          <div className="md:col-span-2">
            <label htmlFor="conta" className="block text-sm font-medium mb-1">
              Conta *
            </label>
            <select
              id="conta"
              value={formData.conta}
              onChange={(e) => handleInputChange('conta', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.conta ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Selecione uma conta</option>
              {contas.map(conta => (
                <option key={conta.id} value={conta.id}>
                  {conta.codigo} - {conta.descricao}
                </option>
              ))}
            </select>
            {errors.conta && (
              <p className="mt-1 text-sm text-red-600">{errors.conta}</p>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading}
           aria-label="Cancelar">
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
           aria-label="Salvar">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            <span className="hidden sm:inline">
              {loading ? 'Salvando...' : 'Salvar'}
            </span>
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Criação"
        message={`Deseja realmente criar a conta a pagar "${formData.descricao}" no valor de R$ ${parseFloat(formData.valor || 0).toFixed(2).replace('.', ',')}?`}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
        danger={false}
      />
    </div>
  );
};

export default ContaPagarCreate;
